import { useEffect, useRef, useState, type ReactNode } from "react";
import { plural, useT } from "../i18n/index.tsx";
import { chainName, formatTime, formatTimeFull, formatUtc, shorten, statusLabel } from "../lib/format.ts";
import { IconAlert, IconCheck, IconCopy, IconExternal, IconInfo } from "./Icons.tsx";
import { chainLogo, mascotSuccess } from "../assets/images.ts";

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for non-secure origins (http via port forwarding).
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    (document.querySelector("dialog[open]") ?? document.body).append(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

export function CopyButton({ value, label, showText = false, className = "" }: { value: string; label?: string; showText?: boolean; className?: string }) {
  const c = useT().common;
  label ??= c.copy;
  const [done, setDone] = useState(false);
  const t = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(t.current), []);
  return (
    <button
      type="button"
      className={`copy-btn ${showText ? "with-text" : ""} ${done ? "done" : ""} ${className}`}
      onClick={async (e) => {
        e.stopPropagation();
        if (await copyText(value)) {
          setDone(true);
          clearTimeout(t.current);
          t.current = setTimeout(() => setDone(false), 1600);
        }
      }}
      aria-label={done ? c.copied : label}
      title={done ? c.copied : label}
    >
      {done ? <IconCheck size={showText ? 16 : 15} /> : <IconCopy size={showText ? 16 : 15} />}
      {showText && <span>{done ? c.copied : label}</span>}
      {!showText && <span className="sr-only" aria-live="polite">{done ? c.copied : ""}</span>}
    </button>
  );
}

/** A player: the merchant's username when they send one (player_name), with the player_ref under it; else just the ref. */
export function Player({ refId, name, copy = false }: { refId: string | null | undefined; name?: string | null; copy?: boolean }) {
  if (!refId) return <span className="muted">–</span>;
  const ref = copy ? <span className="hash"><span className={`mono-sm ${name ? "muted" : ""}`}>{refId}</span><CopyButton value={refId} /></span> : null;
  if (!name) return ref ?? <span className="mono-sm">{refId}</span>;
  return (
    <span className="player">
      <span className="player-name">{name}</span>
      {ref ?? <span className="mono-sm muted">{refId}</span>}
    </span>
  );
}

/** Shortened address / hash in mono, full value on hover, copy button, optional explorer link. */
export function Hash({ value, href, full = false, copy = true }: { value: string | null | undefined; href?: string | null; full?: boolean; copy?: boolean }) {
  if (!value) return <span className="muted">–</span>;
  const text = full ? value : shorten(value);
  return (
    <span className={`hash ${full ? "hash-full" : ""}`}>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer noopener" title={value} className="mono" onClick={(e) => e.stopPropagation()}>
          {text}
        </a>
      ) : (
        <span className="mono" title={value}>{text}</span>
      )}
      {copy && <CopyButton value={value} />}
    </span>
  );
}

const TONE: Record<string, string> = {
  confirmed: "green", completed: "green", delivered: "green", active: "green",
  pending: "amber", pending_approval: "amber", detected: "amber", enrolling: "amber",
  queued: "blue", broadcast: "blue",
  failed: "red", rejected: "red", flagged: "red", orphaned: "red",
};
export function StatusPill({ status, label }: { status: string; label?: string }) {
  return <span className={`pill pill-${TONE[status] ?? "grey"}`}>{label ?? statusLabel(status)}</span>;
}

export function ChainLogo({ chain, size = 20 }: { chain: string; size?: number }) {
  const src = chainLogo(chain);
  if (!src) return <span className="chain-dot" style={{ width: size, height: size }} aria-hidden="true">{chain.slice(0, 1).toUpperCase()}</span>;
  return <img className="chain-logo" src={src} alt="" width={size} height={size} />;
}

/** "USDT" with chain logo and chain name, e.g. for table cells. */
export function AssetChip({ asset, chain, compact = false }: { asset: string; chain?: string; compact?: boolean }) {
  const [c = "", token = asset] = asset.includes(":") ? asset.split(":") : [chain ?? "", asset];
  return (
    <span className="asset-chip" title={asset}>
      <ChainLogo chain={chain ?? c} size={18} />
      <span className="asset-token">{token}</span>
      {!compact && <span className="asset-chain">{chainName(chain ?? c)}</span>}
    </span>
  );
}

export function Alert({ tone = "info", title, children, action }: { tone?: "info" | "warn" | "error" | "success"; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={`alert alert-${tone}`} role={tone === "error" ? "alert" : undefined}>
      <span className="alert-icon">{tone === "success" ? <IconCheck size={18} /> : tone === "info" ? <IconInfo size={18} /> : <IconAlert size={18} />}</span>
      <div className="alert-body">
        {title && <strong>{title}</strong>}
        {children && <div>{children}</div>}
      </div>
      {action && <div className="alert-action">{action}</div>}
    </div>
  );
}

export function EmptyState({ title, children, art = false, action }: { title: string; children?: ReactNode; art?: boolean; action?: ReactNode }) {
  return (
    <div className="empty">
      {art && <img src={mascotSuccess} decoding="async" alt="" width={120} height={120} className="empty-art" />}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  const c = useT().common;
  return <span className="spinner" role="status" aria-label={label ?? c.loading} />;
}

export function PageHeader({ title, eyebrow, children, actions }: { title: string; eyebrow?: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div className="page-head-text">
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {children && <p className="page-sub">{children}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

/** Label / value pairs for detail drawers. */
export function Details({ rows }: { rows: [ReactNode, ReactNode, boolean?][] }) {
  return (
    <dl className="details">
      {rows.map(([k, v, wide], i) => (
        <div key={i} className={wide ? "wide" : undefined}>
          <dt>{k}</dt>
          <dd>{v ?? <span className="muted">–</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" className="ext-link">
      {children} <IconExternal size={14} />
    </a>
  );
}

export function LoadMore({ hasMore, loading, onClick, count }: { hasMore: boolean; loading: boolean; onClick: () => void; count: number }) {
  const c = useT().common;
  if (!count) return null;
  return (
    <div className="load-more">
      {hasMore ? (
        <button type="button" className="btn btn-ghost" onClick={onClick} disabled={loading}>
          {loading ? <Spinner /> : null} {c.loadMore}
        </button>
      ) : (
        <span className="muted small">{plural(count, c.showingAll)}</span>
      )}
    </div>
  );
}

/** Local time; the UTC time is in the hover title. */
export function Time({ iso, full = false }: { iso: string | null | undefined; full?: boolean }) {
  if (!iso) return <span className="muted">–</span>;
  return (
    <time dateTime={iso} title={formatUtc(iso)} className="nowrap">
      {full ? formatTimeFull(iso) : formatTime(iso)}
    </time>
  );
}
